// Hook personalizado para obtener los chatrooms del usuario.
// Usa useQuery de TanStack con supabase.rpc('get_my_chatrooms_alt') como en el código legacy.
// Mantiene compatibilidad con el manejo de estados del sistema original.
// Incluye canal Realtime para actualizaciones automáticas cuando llegan nuevos mensajes.
// Adaptado para web desde amarsi-app.

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { chatService } from '../../services/chatService';
import { useAuthQuery } from '../useAuthQuery';
import { supabase } from '../../lib/supabase';
import { useEffect, useRef } from 'react';

export function useChatRooms() {
  // Obtiene la sesión del usuario para habilitar la query solo si está autenticado.
  const { sessionQuery } = useAuthQuery();
  const session = sessionQuery.data?.session;
  
  // Referencia para almacenar el canal Realtime
  const channelRef = useRef(null);

  const queryClient = useQueryClient();

  // Efecto para configurar canal Realtime que escuche nuevos mensajes
  useEffect(() => {
    if (!session?.user?.id) {
      console.log(' --- useChatRooms: No hay sesión, no se configura canal Realtime');
      return;
    }

    const userId = session.user.id;
    console.log(' --- useChatRooms: Configurando canal Realtime para usuario:', userId);

    // Limpiar canal anterior si existe
    if (channelRef.current) {
      console.log(' --- useChatRooms: Limpiando canal Realtime anterior');
      supabase.removeChannel(channelRef.current);
    }

    // Crear canal para escuchar nuevos mensajes donde el usuario es destinatario
    const channel = supabase
      .channel(`chatrooms_${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT', // Solo nos interesa cuando se crean nuevos mensajes
          schema: 'public',
          table: 'mensajitos',
          filter: `destinatario=eq.${userId}`, // Filtrar por destinatario = usuario actual
        },
        (payload) => {
          console.log(' --- useChatRooms: Nuevo mensaje recibido en Realtime:', payload);
          
          // Invalidar cache para forzar actualización de la lista de chats
          chatService.invalidateAllChatCache();
          
          // Invalidar query en TanStack para refrescar datos
          queryClient.invalidateQueries(['chatrooms', userId]);
          
          console.log(' --- useChatRooms: Cache invalidado y query refrescada');
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE', // También cuando se marcan mensajes como leídos
          schema: 'public',
          table: 'mensajitos',
          filter: `destinatario=eq.${userId}`,
        },
        (payload) => {
          console.log(' --- useChatRooms: Mensaje actualizado en Realtime:', payload);
          
          // Solo refrescar si el cambio afecta el estado de lectura
          if (payload.new?.is_leido !== payload.old?.is_leido) {
            chatService.invalidateAllChatCache();
            queryClient.invalidateQueries(['chatrooms', userId]);
            console.log(' --- useChatRooms: Estado de lectura cambiado, refrescando lista');
          }
        }
      )
      .subscribe((status) => {
        console.log(' --- useChatRooms: Status del canal Realtime:', status);
        if (status === 'SUBSCRIBED') {
          console.log(' --- useChatRooms: ✅ Canal Realtime suscrito exitosamente');
        } else if (status === 'CHANNEL_ERROR') {
          console.error(' --- useChatRooms: ❌ Error en canal Realtime');
        }
      });

    channelRef.current = channel;

    // Cleanup al desmontar
    return () => {
      console.log(' --- useChatRooms: Limpiando canal Realtime');
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [session?.user?.id, queryClient]);

  return useQuery({
    queryKey: ['chatrooms', session?.user?.id],
    queryFn: async () => {
      console.log(' --- getMisChats( inicio: session )', session?.user?.id);
      console.log(' ');
      
      try {
        // El servicio ahora maneja cache inteligente y validaciones automáticamente
        const data = await chatService.getMyChatrooms(session);
        
        // El servicio ya maneja el caso de array vacío, solo pasamos los datos
        console.log(' --- getMisChats: Datos recibidos del servicio:', data);
        console.log(' ');
        return data;
        
      } catch (error) {
        console.log(' --- getMisChats(error) ', error);
        console.log(' ');
        throw error;
      }
    },
    enabled: !!session?.user?.id,
    // Reducir staleTime para que las actualizaciones de Realtime se reflejen rápido
    staleTime: 10 * 1000, // 10 segundos para mayor frescura
    gcTime: 5 * 60 * 1000, // 5 minutos cache
    retry: 0, // Sin retry en TanStack, el servicio maneja reintentos inteligentes
  });
}
